import type { Survey } from '@confer/lib'

import { ensureSurveyRuntimeV1, SURVEY_RUNTIME_VERSION } from './runtime'

export const SURVEY_BUNDLE_CONTRACT = 'confer-survey-v1' as const

export interface SurveyBundleManifestV1 {
  contract: typeof SURVEY_BUNDLE_CONTRACT
  surveyName: string
  runtimeVersion: typeof SURVEY_RUNTIME_VERSION
  releaseId: string
  entryUrl: string
  settingsSurveyName?: string
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

export function parseSurveyBundleManifest(
  value: unknown,
  expectedSurveyName: string,
): SurveyBundleManifestV1 {
  if (!isRecord(value)) throw new Error('Survey bundle manifest must be an object')
  if (value.contract !== SURVEY_BUNDLE_CONTRACT) {
    throw new Error(`Unsupported survey bundle contract ${String(value.contract)}`)
  }
  if (value.runtimeVersion !== SURVEY_RUNTIME_VERSION) {
    throw new Error(`Unsupported survey runtime ${String(value.runtimeVersion)}`)
  }
  if (value.surveyName !== expectedSurveyName) {
    throw new Error(`Survey manifest name ${String(value.surveyName)} does not match the route`)
  }
  if (
    value.settingsSurveyName !== undefined &&
    (typeof value.settingsSurveyName !== 'string' ||
      !/^[\p{L}\p{N}_-]+$/u.test(value.settingsSurveyName))
  ) {
    throw new Error('Survey manifest has an invalid settings survey name')
  }
  if (typeof value.releaseId !== 'string' || !/^[a-f0-9]{12}$/.test(value.releaseId)) {
    throw new Error('Survey manifest has an invalid release ID')
  }

  const expectedEntryPrefix = `/survey-bundles/${encodeURIComponent(expectedSurveyName)}/${value.releaseId}/`
  if (
    typeof value.entryUrl !== 'string' ||
    !value.entryUrl.startsWith(expectedEntryPrefix) ||
    !value.entryUrl.endsWith('.js') ||
    value.entryUrl.includes('..') ||
    value.entryUrl.includes('?') ||
    value.entryUrl.includes('#')
  ) {
    throw new Error('Survey manifest has an invalid entry URL')
  }

  return value as unknown as SurveyBundleManifestV1
}

export async function loadRemoteSurvey(surveyName: string): Promise<Survey | null> {
  const manifestUrl = `/survey-manifests/${encodeURIComponent(surveyName)}.json`
  const response = await fetch(manifestUrl, { cache: 'no-cache' })

  if (response.status === 404) return null
  if (!response.ok) {
    throw new Error(`Unable to load survey manifest (${response.status})`)
  }

  const contentType = response.headers.get('content-type') ?? ''
  // The SPA fallback returns index.html for an unknown static manifest.
  if (contentType.includes('text/html')) return null
  if (!contentType.includes('application/json')) {
    throw new Error(`Unexpected survey manifest content type ${contentType}`)
  }

  const manifest = parseSurveyBundleManifest(await response.json(), surveyName)
  ensureSurveyRuntimeV1()

  const surveyModule = (await import(/* @vite-ignore */ manifest.entryUrl)) as {
    default?: Survey
  }
  const survey = surveyModule.default
  const expectedSettingsSurveyName = manifest.settingsSurveyName ?? surveyName
  if (!survey || survey.settings?.surveyName !== expectedSettingsSurveyName) {
    throw new Error(`Survey bundle ${manifest.releaseId} exported the wrong survey`)
  }

  return survey
}
